/* ============================================================
   acg-ajax-cart.js
   Shared async add-to-cart for the ACG custom theme.

   Used by:
     - sections/acg-main-product.liquid   (PDP form + sticky bar button)
     - sections/acg-featured-products.liquid (product card forms)

   Behaviour:
     - Intercepts submit of any <form> carrying the [data-acg-cart-form]
       attribute, POSTs FormData to /cart/add.js (Accept: application/json).
     - Button feedback: "Adding…" while pending, "Added ✓" on success,
       then restored. Disabled during the request.
     - Refreshes every header cart count (.acg-nav__cart-count) from
       /cart.js after a successful add.
     - On error, shows the Shopify-returned message and re-enables the
       button. Never shows success on a failed request.
     - Messages render in a [data-acg-cart-status] region that the
       section markup places near the button. The region is
       role="status" aria-live="polite" so screen readers announce it.

   This file is intentionally framework-free and idempotent: it can be
   loaded once per page and binds via a single delegated listener.
   ============================================================ */
(function () {
  'use strict';

  if (window.ACGAjaxCart && window.ACGAjaxCart.initialised) {
    return;
  }

  var ADDING_LABEL = 'Adding…';
  var ADDED_LABEL = 'Added ✓';
  var ADDED_HOLD_MS = 1800;
  var STATUS_CLEAR_MS = 6000;

  /* ---- Helpers ---------------------------------------------------- */

  function getStatusRegion(form) {
    // A status region explicitly linked to this form, else the closest one.
    var id = form.getAttribute('data-acg-status-target');
    if (id) {
      var byId = document.getElementById(id);
      if (byId) {
        return byId;
      }
    }
    var scope = form.closest('[data-acg-cart-scope]') || form.parentNode;
    return scope ? scope.querySelector('[data-acg-cart-status]') : null;
  }

  function setStatus(region, message, isError) {
    if (!region) {
      return;
    }
    region.textContent = message || '';
    region.classList.toggle('acg-cart-status--error', !!isError);
    region.classList.toggle('acg-cart-status--visible', !!message);
    if (region._acgClearTimer) {
      clearTimeout(region._acgClearTimer);
    }
    if (message) {
      region._acgClearTimer = setTimeout(function () {
        region.textContent = '';
        region.classList.remove('acg-cart-status--visible', 'acg-cart-status--error');
      }, STATUS_CLEAR_MS);
    }
  }

  function updateCartCounts(itemCount) {
    var counts = document.querySelectorAll('.acg-nav__cart-count');
    Array.prototype.forEach.call(counts, function (el) {
      el.textContent = itemCount;
      if (itemCount > 0) {
        el.removeAttribute('hidden');
      } else {
        el.setAttribute('hidden', '');
      }
    });
    // Keep the cart link's accessible label in sync with the new count.
    var cartLinks = document.querySelectorAll('.acg-nav__cart');
    Array.prototype.forEach.call(cartLinks, function (link) {
      var noun = itemCount === 1 ? 'item' : 'items';
      link.setAttribute('aria-label', 'Cart — ' + itemCount + ' ' + noun);
    });
  }

  function refreshCartCount() {
    return fetch(window.Shopify && window.Shopify.routes
        ? window.Shopify.routes.root + 'cart.js'
        : '/cart.js', {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin'
    })
      .then(function (res) { return res.json(); })
      .then(function (cart) {
        if (cart && typeof cart.item_count === 'number') {
          updateCartCounts(cart.item_count);
        }
        return cart;
      })
      .catch(function () { /* count refresh is non-critical */ });
  }

  function errorMessageFrom(payload) {
    // /cart/add.js error shape: { status, message, description }
    if (payload && typeof payload === 'object') {
      return payload.description || payload.message || '';
    }
    return '';
  }

  /* ---- Submit handler --------------------------------------------- */

  function handleSubmit(form) {
    if (form._acgSubmitting) {
      return;
    }

    var button = form.querySelector('[type="submit"], button:not([type])');
    var region = getStatusRegion(form);
    var originalLabel = button ? button.innerHTML : '';
    var originalAriaDisabled = button ? button.getAttribute('aria-disabled') : null;

    function restoreButton() {
      if (!button) {
        return;
      }
      button.disabled = false;
      if (originalAriaDisabled === null) {
        button.removeAttribute('aria-disabled');
      } else {
        button.setAttribute('aria-disabled', originalAriaDisabled);
      }
      button.innerHTML = originalLabel;
    }

    form._acgSubmitting = true;
    if (button) {
      button.disabled = true;
      button.setAttribute('aria-disabled', 'true');
      button.textContent = ADDING_LABEL;
    }
    setStatus(region, '', false);

    var addUrl = (window.Shopify && window.Shopify.routes)
      ? window.Shopify.routes.root + 'cart/add.js'
      : '/cart/add.js';

    fetch(addUrl, {
      method: 'POST',
      headers: { Accept: 'application/json' },
      body: new FormData(form),
      credentials: 'same-origin'
    })
      .then(function (response) {
        return response.json().then(function (data) {
          return { ok: response.ok, status: response.status, data: data };
        });
      })
      .then(function (result) {
        if (!result.ok || (result.data && result.data.status)) {
          // Shopify returned an error (sold out, stock limit, etc.)
          var msg = errorMessageFrom(result.data)
            || 'Sorry, this item could not be added to your cart.';
          setStatus(region, msg, true);
          restoreButton();
          form._acgSubmitting = false;
          document.dispatchEvent(new CustomEvent('acg:cart:error', {
            detail: { message: msg }
          }));
          return;
        }

        // Success — confirm, refresh header count, then restore the button.
        if (button) {
          button.textContent = ADDED_LABEL;
        }
        setStatus(region, 'Added to your cart.', false);

        refreshCartCount().then(function () {
          // Notify the cart drawer (or any other listener) to open and
          // refresh. The inline "Added ✓" confirmation stays as-is for
          // immediate near-button feedback during the brief moment
          // before the drawer slides in.
          document.dispatchEvent(new CustomEvent('acg:cart:added', {
            detail: {
              addedItem: result.data,
              item: result.data
            }
          }));
        });

        setTimeout(function () {
          restoreButton();
          form._acgSubmitting = false;
        }, ADDED_HOLD_MS);
      })
      .catch(function () {
        var netMsg = 'Something went wrong. Please try again or refresh the page.';
        setStatus(region, netMsg, true);
        restoreButton();
        form._acgSubmitting = false;
        document.dispatchEvent(new CustomEvent('acg:cart:error', {
          detail: { message: netMsg }
        }));
      });
  }

  /* ---- Delegated binding ------------------------------------------ */

  document.addEventListener('submit', function (e) {
    var form = e.target;
    if (!form || !form.matches || !form.matches('form[data-acg-cart-form]')) {
      return;
    }
    e.preventDefault();
    handleSubmit(form);
  });

  // Public hook so a section can trigger an add programmatically
  // (used by the PDP sticky-bar button, which targets the PDP form).
  window.ACGAjaxCart = {
    initialised: true,
    submitForm: function (form) {
      if (form && form.matches && form.matches('form[data-acg-cart-form]')) {
        handleSubmit(form);
      }
    },
    refreshCartCount: refreshCartCount
  };
}());
